import React, { useState } from 'react';
import {
    useParams,
    useLocation
} from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import useMediaQuery from '@mui/material/useMediaQuery';
import useTheme from '@mui/material/styles/useTheme';
import MoreVert from '@mui/icons-material/MoreVert';
import jsPDF from 'jspdf';
import QrCode from 'qrcode';
import {
    useQuery,
    useQueryClient,
    UseQueryResult
} from 'react-query';
import {
    useNavigate,
    useOutletContext
} from 'react-router-dom';
import ClassTable from 'ui/components/classes-components/ClassTable';
import NavBar from 'ui/components/classes-components/NavBar';
import { QrCodesResponse, QrCodesUpdated } from 'ui/components/classes-components/QrCodes';
import PopupWithCloseButton from 'ui/components/classes-components/PopupWithCloseButton';
import EditClass from 'ui/components/classes-components/EditClass';
import ClassFlow from 'ui/components/classes-components/ClassFlow';
import DeleteClass from 'ui/components/classes-components/DeleteClass';
import TeachersTable from 'ui/components/classes-components/TeachersTable';
// import TeacherGuide from 'ui/components/classes-components/TeacherGuide';
// import StudentGuide from 'ui/components/classes-components/StudentGuide';
import { IOutletContext } from 'ui/components/Dashboard';
import Students from 'ui/pages/Students';
import IClass from 'models/IClass';
import IProfile from 'models/IProfile';
import {
    CLASS_ADD_STUDENTS_BUTTON,
    CLASS_MENU_DELETE,
    CLASS_MENU_EDIT,
    CLASS_VIEW_DOWNLOAD_PDF_QR,
    CLASS_VIEW_STUDENTS,
    CLASS_VIEW_TEACHER,
    // CLASS_VIEW_STUDENT_GUIDE,
    // CLASS_VIEW_TEACHER_GUIDE,
    DELETE_CLASS_POPUP_HEADER,
    EDIT_CLASS_POPUP_HEADER,
    QR_CODE_POPUP_HEADER,
    STUDENTS_TABLE_DONE_BUTTON,
} from 'config/config';
import { HttpClient } from 'apiclient/ApiClient';
import { CircularProgress } from '@mui/material';



export default function ClassView() {
    const id = useParams();
    const httpClient = HttpClient();
    const loc = useLocation();
    const theme = useTheme();
    const matches = useMediaQuery(theme.breakpoints.up('sm'));
    const [dialogOpen, setDialogOpen] = React.useState(
        typeof (loc.state as { popup?: boolean })?.popup === 'boolean' ?
            (loc.state as { popup: boolean }).popup :
            false
    )
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const [menuOpen, setMenuOpen] = React.useState(false);
    const [popupOpen, setPopupOpen] = React.useState(false);
    const [popupElement, setPopupElement] = React.useState<null | JSX.Element>(null);
    const [popupTitle, setPopupTitle] = React.useState('');
    const [flowOpen, setFlowOpen] = React.useState(false);
    //const [selectedProfileId, setSelectedProfileId] = React.useState('');
    const profilesGet: UseQueryResult<IProfile[]> = useQuery(
        `/school/classes/${id.id}/members`,
        async () => await httpClient.get(`/school/classes/${id.id}/members`)
    );
    const studentsCount = profilesGet.data?.filter(val => val.profile_type === 'student').length;
    const navigate = useNavigate();
    const {
        setBgcolor,
        setTopBarColorLight
    } = useOutletContext<IOutletContext>();
    React.useEffect(() => {
        setBgcolor('background.paper');
        setTopBarColorLight("background.paper");
    }, [setBgcolor, setTopBarColorLight]);
    const closePopup = () => {
        setPopupOpen(false);
        setPopupTitle('');
        setPopupElement(null);
    };
    const queryClient = useQueryClient();
    const classesGet: UseQueryResult<IClass[]> = useQuery(
        'classes', async () => await httpClient.get('/school/classes'),
    );
    const thisClass = classesGet.data?.filter(val => { return val.id === id.id })[0];
    const invalidate = () => {
        queryClient.invalidateQueries('classes')
    };
    const [isPdfLoading, setIsPdfLoading] = useState(false);
    
    // Check for auto-download parameter (HashRouter handles query params automatically)
    const searchParams = new URLSearchParams(loc.search);
    const autoDownloadPdf = searchParams.get('auto_download_pdf') === 'true';
    
    const hasAutoDownloaded = React.useRef(false);
    
    // PDF generation function
    const generateAndDownloadPdf = React.useCallback(async () => {
        if (isPdfLoading) return;
        
        setIsPdfLoading(true);
        try {
            const loginData = await httpClient.get<QrCodesResponse>(`/school/classes/${id.id}/deeplinks`);
            const doc = new jsPDF();
            doc.setFontSize(20);
            doc.text(loginData.class_name, doc.internal.pageSize.getWidth() / 2, 20, { align: 'center' });
            const pageHeight = doc.internal.pageSize.height;

            const qrSize = 50;
            const margin = 15;
            const columns = 3;
            let x = margin;
            let y = 40;
            let index = -1;
            
            const students = loginData.members.filter(m => m.role === 'student');
            
            for (const student of students) {
                index += 1;
                const qrCodeDataURL = await QrCode.toDataURL(student.url);
                doc.addImage(qrCodeDataURL, 'PNG', x, y, qrSize, qrSize, undefined, 'SLOW');
                doc.setFontSize(12);
                doc.text(student.name, x + qrSize / 2, y + qrSize + 2, { align: 'center' });
                
                x += qrSize + margin;
                if ((index + 1) % columns === 0) {
                    x = margin;
                    y += qrSize + 15;
                }
                if (y > pageHeight - 50) {
                    doc.addPage();
                    y = 10;
                }
            }
            
            doc.save(`${loginData.class_name}_QR_Codes.pdf`);
        } catch (error) {
            console.error('[PDF] Failed to generate PDF:', error);
        } finally {
            setIsPdfLoading(false);
        }
    }, [httpClient, id.id, isPdfLoading]);
    
    // Auto-download effect - trigger immediately when component mounts if parameter is present
    React.useEffect(() => {
        
        if (autoDownloadPdf && !hasAutoDownloaded.current && !isPdfLoading) {
            hasAutoDownloaded.current = true;
            // Use setTimeout to ensure the component is fully mounted
            setTimeout(() => {
                generateAndDownloadPdf();
            }, 500);
        }
    }, [autoDownloadPdf, isPdfLoading, generateAndDownloadPdf]);
    return (
        <Container maxWidth='lg' sx={{ pt: 2 }}>
            <Box sx={{ px: matches ? 6 : 0 }}>
                <NavBar nodesMap={new Map([[id.id || '', thisClass?.name || '...']])} />
                <Box sx={{
                    display: 'flex',
                    alignItems: 'baseline',
                    justifyContent: "space-between",
                    flexDirection: 'column',
                    mb: 1,
                    mt: 6
                }}>
                    <Box display='flex' sx={{ alignItems: 'baseline', justifyContent: 'space-between' }} width='100%'>
                        <Box>
                            <Typography variant="h5" fontWeight={500} fontSize={32}>
                                {thisClass && thisClass.name}
                            </Typography>
                            <Typography variant="h5" fontWeight={300} fontSize={16}>
                                {studentsCount || '0'} Student{studentsCount === 1 ? '' : 's'} • Class code: {thisClass && thisClass.code}
                            </Typography>
                        </Box>
                        {thisClass?.source === 'teacher' && (
                        <Box>
                            <IconButton onClick={(e) => {
                                setAnchorEl(e.currentTarget);
                                setMenuOpen(true)
                            }}>
                                <MoreVert />
                            </IconButton>
                            <Menu
                                id="basic-menu"
                                anchorEl={anchorEl}
                                open={menuOpen}
                                onClose={() => setMenuOpen(false)}
                                MenuListProps={{
                                    'aria-labelledby': 'basic-button',
                                }}
                            >
                                <MenuItem onClick={() => {
                                    setMenuOpen(false);
                                    setPopupTitle(DELETE_CLASS_POPUP_HEADER);
                                    setPopupElement(
                                        <DeleteClass
                                            closePopup={closePopup}
                                            classId={id.id || ''}
                                            className={thisClass?.name || ''}
                                            invalidate={() => {
                                                navigate('/classes')
                                                invalidate();
                                            }}
                                        />
                                    )
                                    setPopupOpen(true);
                                }}>
                                    {CLASS_MENU_DELETE}
                                </MenuItem>
                                <MenuItem onClick={() => {
                                    setMenuOpen(false);
                                    setPopupTitle(EDIT_CLASS_POPUP_HEADER);
                                    setPopupElement(
                                        <EditClass
                                            closePopup={closePopup}
                                            classId={id.id || ''}
                                            classData={thisClass || {} as IClass}
                                            invalidate={invalidate}
                                        />
                                    );
                                    setPopupOpen(true);
                                }}>
                                    {CLASS_MENU_EDIT}
                                </MenuItem>
                            </Menu>
                            <Button
                                onClick={generateAndDownloadPdf}
                                variant='contained'
                                disabled={isPdfLoading}
                            >
                                {isPdfLoading ? <CircularProgress size={20} /> : CLASS_VIEW_DOWNLOAD_PDF_QR}
                            </Button>
                        </Box>
                        )}
                    </Box>
                    <br />
                </Box>
                <Box display='flex' sx={{ alignItems: 'center', justifyContent: 'space-between' }} width='100%' height={56}>
                    <Typography variant='subtitle2'>
                        {CLASS_VIEW_TEACHER}
                    </Typography>
                    {/* "Add educators" appears here when the co-teacher flow (#350) ships —
                        no disabled placeholder until then. */}
                    {/* <Link sx={{ textDecoration: 'none', cursor: 'pointer' }} onClick={() => {
                        setPopupElement(<TeacherGuide />)
                        setPopupTitle(CLASS_VIEW_TEACHER_GUIDE);
                        setPopupOpen(true);
                    }}>
                        {CLASS_VIEW_TEACHER_GUIDE}
                    </Link> */}
                </Box>
                <TeachersTable
                    classId={id.id}
                    showQrCode={(prof_id: string) => {
                        setPopupElement(
                            <QrCodesUpdated
                                class_id={id.id || ''}
                                selectedProfileId={prof_id}
                            />
                        )
                        setPopupTitle(QR_CODE_POPUP_HEADER);
                        setPopupOpen(true);
                    }}
                />
                <Box display='flex' sx={{ alignItems: 'center', justifyContent: 'space-between', mt: 6 }} width='100%' height={56}>
                    <Typography variant='subtitle2'>
                        {CLASS_VIEW_STUDENTS}
                    </Typography>
                    {thisClass?.source === 'teacher' && (
                        <Button
                            variant='contained'
                            disableElevation
                            onClick={() => setFlowOpen(true)}
                            disabled={
                                profilesGet.data?.length && profilesGet.data?.filter(prof => prof.profile_type === 'student').length < 40 ? false : true
                            }
                            sx={{
                                bgcolor: 'rgba(106, 86, 204, 0.1)',
                                color: 'primary.main',
                                fontWeight: 700,
                                '&:hover': { bgcolor: 'rgba(106, 86, 204, 0.18)' },
                            }}
                        >
                            {CLASS_ADD_STUDENTS_BUTTON}
                        </Button>
                    )}
                </Box>
                <ClassTable
                    profiles={profilesGet}
                    classId={id.id}
                    classSource={thisClass?.source || "teacher"}
                    showQrCode={(prof_id: string) => {
                        setPopupElement(
                            <QrCodesUpdated
                                class_id={id.id || ''}
                                selectedProfileId={prof_id}
                            />
                        )
                        setPopupTitle(QR_CODE_POPUP_HEADER);
                        setPopupOpen(true);
                    }}
                />
            </Box>
            <Dialog
                fullScreen
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
            >
                <Button
                    onClick={() => {
                        queryClient.invalidateQueries(`/classes/${id.id}`);
                        setDialogOpen(false);
                    }}
                    variant='contained'
                    sx={{ ml: 'auto', mr: 4, my: 1 }}
                >
                    {STUDENTS_TABLE_DONE_BUTTON}
                </Button>
                <Container maxWidth='xl' sx={{ pt: 8 }}>
                    <Students classId={id.id} source={thisClass?.source} />
                </Container>
            </Dialog>
            <Dialog
                open={popupOpen}
                onClose={closePopup}
            >
                <PopupWithCloseButton
                    closePopap={closePopup}
                    title={popupTitle}
                >
                    {popupElement || <></>}
                </PopupWithCloseButton>
            </Dialog>
            <ClassFlow
                open={flowOpen}
                initialStep="add"
                classId={id.id}
                grade={thisClass?.number}
                onClose={() => setFlowOpen(false)}
                onFinish={() => {
                    setFlowOpen(false);
                    queryClient.invalidateQueries(`/school/classes/${id.id}/members`);
                }}
            />
        </Container>
    );
};